// Phase 9B — BoardView.
//
// Shared board shell that renders any of the 6 board types.
// Features: board header with Bebas Neue title, stat row (KPIs),
// status tab filter, grid layout of BoardItemCards, empty state,
// and phantom badge.
//
// Each board type gets a unique title, subtitle, and accent
// treatment while sharing the same structural layout.

import { useState, useMemo } from "react";
import type { CSSProperties } from "react";
import type {
  BoardType,
  BoardItem,
  BoardItemStatus,
  UseBoardItemsState,
} from "../../boards/useBoardItems.ts";
import { BoardItemCard, STATUS_DOT_COLORS } from "./BoardItemCard.tsx";
import * as s from "./board.styles.ts";

// ─── Board metadata ────────────────────────────────────────────

interface BoardMeta {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyNote: string;
}

const BOARD_META: Record<BoardType, BoardMeta> = {
  opportunities: {
    title: "Market Opportunities",
    subtitle: "Signal-driven market intelligence pipeline",
    emptyTitle: "No Opportunities",
    emptyNote: "Market opportunities will appear here as signals are generated and scored.",
  },
  chains: {
    title: "Rollover Chains",
    subtitle: "Compounding chain lifecycle tracker",
    emptyTitle: "No Chains",
    emptyNote: "ROLLOVER chain items will populate as cycles and chains are created.",
  },
  risk: {
    title: "Risk Board",
    subtitle: "Position risk and exposure monitor",
    emptyTitle: "No Risk Items",
    emptyNote: "Risk items will surface as positions and exposures are tracked.",
  },
  results: {
    title: "Results & Resolution",
    subtitle: "Outcome tracking and settlement ledger",
    emptyTitle: "No Results",
    emptyNote: "Results will appear as signals and chains resolve.",
  },
  goals: {
    title: "User Goals",
    subtitle: "Personal targets and milestone tracker",
    emptyTitle: "No Goals",
    emptyNote: "Set goals to track your progress and build discipline.",
  },
  admin: {
    title: "Admin Operations",
    subtitle: "System operations and maintenance log",
    emptyTitle: "No Operations",
    emptyNote: "Admin operations will appear as system tasks are generated.",
  },
};

// ─── Status tabs ───────────────────────────────────────────────

type TabKey = "all" | BoardItemStatus;

interface TabDef {
  key: TabKey;
  label: string;
}

const TABS: TabDef[] = [
  { key: "all", label: "All" },
  { key: "detected", label: "Detected" },
  { key: "qualifying", label: "Qualifying" },
  { key: "active", label: "Active" },
  { key: "ready", label: "Ready" },
  { key: "executing", label: "Executing" },
  { key: "live", label: "Live" },
  { key: "monitoring", label: "Monitoring" },
  { key: "in_review", label: "Review" },
  { key: "queued", label: "Queued" },
  { key: "backlog", label: "Backlog" },
  { key: "resolved_win", label: "Won" },
  { key: "resolved_loss", label: "Lost" },
  { key: "learned", label: "Learned" },
  { key: "archived", label: "Archived" },
];

function filterByTab(items: readonly BoardItem[], tab: TabKey): readonly BoardItem[] {
  if (tab === "all") return items;
  return items.filter((i) => i.status === tab);
}

// ─── Stats computation ─────────────────────────────────────────

interface BoardStats {
  total: number;
  active: number;
  live: number;
  pending: number;
  winRate: string;
}

function computeStats(items: readonly BoardItem[]): BoardStats {
  const total = items.length;
  const active = items.filter((i) =>
    i.status === "active" || i.status === "executing" || i.status === "live" || i.status === "monitoring",
  ).length;
  const live = items.filter((i) => i.status === "live" || i.status === "monitoring").length;
  const pending = items.filter((i) =>
    i.status === "in_review" || i.status === "queued" || i.status === "qualifying" || i.status === "ready",
  ).length;

  const resolved = items.filter((i) =>
    i.status === "resolved_win" || i.status === "resolved_loss" || i.status === "resolved_void",
  );
  const wins = resolved.filter((i) => i.status === "resolved_win").length;
  const winRate = resolved.length > 0
    ? `${((wins / resolved.length) * 100).toFixed(0)}%`
    : "—";

  return { total, active, live, pending, winRate };
}

// ─── Component ─────────────────────────────────────────────────

export interface BoardViewProps {
  boardType: BoardType;
  state: UseBoardItemsState;
  onItemClick?: (item: BoardItem) => void;
  selectedItemId?: string | null;
}

export function BoardView({
  boardType,
  state,
  onItemClick,
  selectedItemId,
}: BoardViewProps): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const meta = BOARD_META[boardType];
  const { items, loading, error } = state;

  const stats = useMemo(() => computeStats(items), [items]);
  const filtered = useMemo(() => filterByTab(items, activeTab), [items, activeTab]);

  if (loading) return null;

  return (
    <div style={s.boardRoot}>
      {/* Header */}
      <div style={s.boardHeader}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={s.boardTitle}>{meta.title}</span>
          <span style={s.boardSubtitle}>{meta.subtitle}</span>
        </div>
        <span style={s.boardCount}>{String(stats.total)} items</span>
      </div>

      {/* Stats row */}
      <div style={s.statRow}>
        <div style={s.statCard}>
          <span style={s.statValue}>{String(stats.active)}</span>
          <span style={s.statLabel}>Active</span>
        </div>
        <div style={s.statCard}>
          <span style={s.statValue}>{String(stats.live)}</span>
          <span style={s.statLabel}>Live</span>
        </div>
        <div style={s.statCard}>
          <span style={s.statValue}>{String(stats.pending)}</span>
          <span style={s.statLabel}>Pending</span>
        </div>
        <div style={s.statCard}>
          <span style={s.statValue}>{stats.winRate}</span>
          <span style={s.statLabel}>Win Rate</span>
        </div>
      </div>

      {/* Tab bar */}
      <div style={s.tabBar}>
        {TABS.map((t) => {
          const count = filterByTab(items, t.key).length;
          if (count === 0 && t.key !== "all" && t.key !== "active" && t.key !== "backlog") {
            return null; // Hide empty tabs (except key ones)
          }
          return (
            <button
              key={t.key}
              type="button"
              style={activeTab === t.key ? s.tabItemActive : s.tabItem}
              onClick={() => setActiveTab(t.key)}
            >
              {t.key !== "all" ? (
                <span
                  style={{
                    ...s.laneDot,
                    background: STATUS_DOT_COLORS[t.key as BoardItemStatus] ?? "var(--sz-text-muted)",
                    display: "inline-block",
                    marginRight: "6px",
                    verticalAlign: "middle",
                  }}
                />
              ) : null}
              {t.label}
              <span style={s.tabCount}>{String(count)}</span>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error !== null ? <span style={s.emptyNote}>{error}</span> : null}

      {/* Content */}
      {filtered.length === 0 ? (
        <div style={s.emptyState}>
          <span style={s.emptyTitle}>{meta.emptyTitle}</span>
          <span style={s.emptyNote}>{meta.emptyNote}</span>
        </div>
      ) : (
        <div style={s.boardGrid}>
          {filtered.map((item) => (
            <BoardItemCard
              key={item.id}
              item={item}
              selected={selectedItemId === item.id}
              onClick={onItemClick}
            />
          ))}
        </div>
      )}

      {/* Phantom badge */}
      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
